/**
 * Redux async actions (src/store/actions/exportActions.js).
 *
 * This module defines Redux Toolkit thunks (createAsyncThunk) that call backend endpoints via the shared axios client.
 * Slices consume the fulfilled payloads in `extraReducers` to update the UI state.
 */

import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../helpers/axiosconfig";

const normalizeExportError = (error, fallback) => ({
  status: Number(error?.response?.status) || null,
  message: error?.response?.data?.message || error?.response?.data?.error || fallback,
});

// Export Students to Excel
/**
 * Async thunk: exportStudentsToExcel.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const exportStudentsToExcel = createAsyncThunk(
  "exports/exportStudentsToExcel",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `export/students/advanced${queryParams ? `?${queryParams}` : ''}`;
      
      const response = await axios.get(url, {
        responseType: 'blob', // Important for file downloads
      });
      
      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `students_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      return { 
        success: true, 
        message: 'Students data exported successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return rejectWithValue(normalizeExportError(error, 'Failed to export students data'));
    }
  }
);

// Export Teachers to Excel
/**
 * Async thunk: exportTeachersToExcel.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const exportTeachersToExcel = createAsyncThunk(
  "exports/exportTeachersToExcel",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('export/teachers', {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `teachers_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      return { 
        success: true, 
        message: 'Teachers data exported successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return rejectWithValue(normalizeExportError(error, 'Failed to export teachers data'));
    }
  }
);

// Export Bookings to Excel (Advanced)
/**
 * Async thunk: exportBookingsToExcel.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const exportBookingsToExcel = createAsyncThunk(
  "exports/exportBookingsToExcel",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `export/bookings/advanced${queryParams ? `?${queryParams}` : ''}`;
      
      const response = await axios.get(url, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `bookings_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      return { 
        success: true, 
        message: 'Bookings data exported successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return rejectWithValue(normalizeExportError(error, 'Failed to export bookings data'));
    }
  }
);

// Export Packages to Excel
/**
 * Async thunk: exportPackagesToExcel.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const exportPackagesToExcel = createAsyncThunk(
  "exports/exportPackagesToExcel",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('export/packages', {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `packages_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      return { 
        success: true, 
        message: 'Packages data exported successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return rejectWithValue(normalizeExportError(error, 'Failed to export packages data'));
    }
  }
);

// Export Payments to Excel
/**
 * Async thunk: exportPaymentsToExcel.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const exportPaymentsToExcel = createAsyncThunk(
  "exports/exportPaymentsToExcel",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('export/payments', {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `payments_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      return { 
        success: true, 
        message: 'Payments data exported successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return rejectWithValue(normalizeExportError(error, 'Failed to export payments data'));
    }
  }
);

// Export Courses to Excel
/**
 * Async thunk: exportCoursesToExcel.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const exportCoursesToExcel = createAsyncThunk(
  "exports/exportCoursesToExcel",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('export/courses', {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `courses_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      return { 
        success: true, 
        message: 'Courses data exported successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return rejectWithValue(normalizeExportError(error, 'Failed to export courses data'));
    }
  }
);

// Export Enquiries to Excel
/**
 * Async thunk: exportEnquiriesToExcel.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const exportEnquiriesToExcel = createAsyncThunk(
  "exports/exportEnquiriesToExcel",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('export/enquiries', {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `enquiries_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      return { 
        success: true, 
        message: 'Enquiries data exported successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return rejectWithValue(normalizeExportError(error, 'Failed to export enquiries data'));
    }
  }
);

// Export All Data to Excel (Complete Export)
/**
 * Async thunk: exportAllDataToExcel.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const exportAllDataToExcel = createAsyncThunk(
  "exports/exportAllDataToExcel",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('export/all', {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `complete_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      return { 
        success: true, 
        message: 'Complete data exported successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return rejectWithValue(normalizeExportError(error, 'Failed to export complete data'));
    }
  }
);
